import moduleStyles from "components/module/styles.module.css";

import React, { useState, useEffect, use }  from 'react';
import { ModuleProps } from "components/props/ModuleProps"
import { IconPicker } from "components/layout/icons/IconPicker";
import parse from 'html-react-parser';
import Spacer from "components/layout/Spacer";
import WorkTimeRegistryApiRepository from '@pilot-web/worktime/api/WorkTimeRegistryApiRepository';
import { isNullOrEmpty } from 'components/helpers/ObjectHelpers';
import { formatDateTimeForDisplay } from 'components/helpers/DateTimeHelpers';
import { IconColors } from 'services/SystemNames';
import { activityisPiloting, getBorderColor, isWorkTimeRest } from '@pilot-web/worktime/';
import { ModuleOnline } from "components/module/ModuleOnline";
import { ModuleWithData } from "components/module/ModuleWithData";
import { AppStore } from "components/stores/AppStore";
import { useStoreSelector } from "components/hooks/useStoreSelector";
import { useNavigate } from "react-router";
import { Module } from "./Module";

interface PromiseProps {
    period: any | null,
    activity: any | null
}

const DefaultPromiseProps: PromiseProps = {
    period: null,
    activity: null,
}

interface DashboardProps {
    refresh: boolean
}


export const Dashboard = ({ refresh }: DashboardProps) => {

    const navigate = useNavigate();

    const [dataPromise, setDataPromise] = useState<null | Promise<PromiseProps>>(null);

    const fetchOnline = async () => {
        let recentResponse: any = null;
        let activityResponse: any = null;

        recentResponse = await WorkTimeRegistryApiRepository.getRecentAsync();

        const recent = await recentResponse.json();
        const result: PromiseProps = {
            period: recent[0] ?? null,
            activity: null
        }

        activityResponse = await WorkTimeRegistryApiRepository.getActivityInfo();
        if (activityResponse.ok) {
            const activity = await activityResponse.json();
            result.activity = activity;
        }

        return result;
    }

    const fetchData = (): Promise<PromiseProps> => {
        if (navigator.onLine) {
            return fetchOnline();
        } else {
            return new Promise<PromiseProps>((resolve) => resolve(DefaultPromiseProps));
        }
    }

    const onClick = () => {
        navigate(Module.url);
    }

    useEffect(() => {
        setDataPromise(fetchData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!refresh) return;
        setDataPromise(fetchData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh]);

    return (
        <ModuleWithData
            module={Module}
            dataPromise={dataPromise}
            errorText="Kunne ikke hente arbeidstid.">
            <DashboardContent
                dataPromise={dataPromise}
                onClick={onClick} />
        </ModuleWithData>
    )
}

interface DashboardProps { 
    dataPromise: Promise<PromiseProps>,
    onClick: () => void,
}
export const DashboardContent = ({ dataPromise, onClick }: DashboardProps) => {

    const data = use<PromiseProps>(dataPromise);
    const darkmode = useStoreSelector(AppStore, (state) => state.darkmode);

    return (
        data &&
        <ModuleOnline module={Module}>
            <div
                onClick={onClick}
                    className={moduleStyles.bar}
                style={{ backgroundColor: getBackgroundColor(), color: getColor() }}>
                    <div className={moduleStyles.barContent}>
                    <div>
                        <div role="heading">Arbeidstid</div>
                        <div role="note">
                            <Spacer height={5} />
                                {
                                    (data.period) ?
                                        <>
                                            {<div>Type: {getName()}</div>}
                                            {<div>Start: {formatDateTimeForDisplay(new Date(data.period.startTime))}</div>}
                                            {<div>Varighet: {isNullOrEmpty(data.period.duration) ? parse("P&aring;g&aring;ende") : data.period.duration}</div>}
                                        </>
                                        :
                                        <div>Ingen arbeidstid er registrert.</div>
                                }

                                {
                                    (data.activity) &&
                                    <>
                                        <Spacer height={5} />
                                        <div>
                                            <u>{data.activity.infoText}</u>
                                        </div>
                                    </>
                                }
                        </div>
                    </div>
                    <div>
                        <IconPicker icon="timer" size={24} />
                    </div>
                </div>
            </div>
        </ModuleOnline>
    )

    function getBackgroundColor() {
        if (isNullOrEmpty(data.period)) return "var(--home-default-bg)";

        if (data.activity) {
            switch (data.activity.level) {
                case 2:
                    return "var(--home-worktime-warning-bg)";
                case 3:
                    return IconColors.Error;
                default:
                    break;
            }
        }
        if (isWorkTimeRest(data.period)) return "var(--home-default-bg)";

        return getBorderColor(data.period);
    }

    function getColor() {
        if (data.activity) {
            if (data.activity) {
                switch (data.activity.level) {
                    case 2:
                        return darkmode ? "#fff" : "#333";
                    case 3:
                        return "#fff";
                    default:
                        break;
                }
            }
        }

        const period = data.period;

        if (isNullOrEmpty(period)) return "var(--font-primary)";

        if (period.activityType.systemName === 'NONE' && period.workTimePeriodType.value === period.WorkingTime) {
            return "#fff";
        }
        else if (period.isOnRequiredRest) {
            return "#333";
        }
        else if (activityisPiloting(period.activityType.systemName)) {
            return "#fff";
        }
        else {
            return "var(--font-primary)";
        }
    }

    function getName() {
        if (data.period.activityType.systemName === "NONE") return data.period.workTimePeriodType.nameNor;
        return data.period.activityType.nameNor;
    }
}
